// Schedule helpers for the admin: game status and warnings about gaps/overlaps.
// Game dates are stored as UTC days; a game is live from startDate through endDate.

export interface ScheduledGame {
  _id: string;
  name: string;
  startDate: string;
  endDate: string;
  published?: boolean;
}

export type GameStatus = "Live" | "Upcoming" | "Past" | "Draft";

const DAY = 86400000;
const utcDay = (d: string | Date) => {
  const x = new Date(d);
  return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
};
export const fmtDay = (ms: number) =>
  new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export const gameStatus = (g: ScheduledGame, now: Date = new Date()): GameStatus => {
  if (g.published === false) return "Draft";
  const today = utcDay(now);
  if (utcDay(g.startDate) > today) return "Upcoming";
  if (utcDay(g.endDate) < today) return "Past";
  return "Live";
};

/** Upcoming first (soonest at top), then live, then past (newest first), drafts last */
export const sortGames = <T extends ScheduledGame>(games: T[], now: Date = new Date()): T[] => {
  const rank: Record<GameStatus, number> = { Live: 0, Upcoming: 1, Past: 2, Draft: 3 };
  return [...games].sort((a, b) => {
    const ra = rank[gameStatus(a, now)], rb = rank[gameStatus(b, now)];
    if (ra !== rb) return ra - rb;
    const da = utcDay(a.startDate), db = utcDay(b.startDate);
    return ra === 1 ? da - db : db - da;
  });
};

/** Plain-language warnings about the published schedule */
export const scheduleWarnings = (games: ScheduledGame[], now: Date = new Date(), lookaheadDays = 7): string[] => {
  const today = utcDay(now);
  const pub = games
    .filter((g) => g.published !== false)
    .map((g) => ({ name: g.name, start: utcDay(g.startDate), end: utcDay(g.endDate) }))
    .sort((a, b) => a.start - b.start);
  const warnings: string[] = [];
  if (pub.length === 0) return ["No published games yet. Players will see an error until one is published."];

  const live = pub.filter((g) => g.start <= today && g.end >= today);
  if (live.length === 0) {
    const last = [...pub].reverse().find((g) => g.end < today);
    warnings.push(
      last
        ? `No puzzle is scheduled for today. Players are still seeing "${last.name}", which ended ${fmtDay(last.end)}.`
        : "No puzzle is scheduled for today."
    );
  }

  const lastEnd = Math.max(...pub.map((g) => g.end));
  if (lastEnd < today + lookaheadDays * DAY) {
    warnings.push(`Nothing is scheduled after ${fmtDay(lastEnd)}. Add next week's puzzle.`);
  }

  for (let i = 1; i < pub.length; i++) {
    const prev = pub[i - 1], cur = pub[i];
    if (cur.end < today) continue; // only care about current and future problems
    if (cur.start <= prev.end) {
      warnings.push(`"${prev.name}" and "${cur.name}" overlap (${fmtDay(cur.start)} to ${fmtDay(Math.min(prev.end, cur.end))}). Players only see one of them.`);
    } else if (cur.start > prev.end + DAY && cur.start > today) {
      warnings.push(`Gap with no new puzzle from ${fmtDay(prev.end + DAY)} to ${fmtDay(cur.start - DAY)}.`);
    }
  }
  return warnings;
};
